import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, MapPin, X, User, Settings, LogOut, Heart, Store, Lightbulb, Send, ShieldAlert, Bell, Plus } from 'lucide-react';
import AuthChoiceModal from './AuthChoiceModal';
import LanguageSelector from './LanguageSelector';
import { useLocation } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../i18n/I18nContext';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const LOGO_URL = "/logo.jpg";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Header({ onSearch, searchQuery = '', showSearch = true }) {
  const navigate = useNavigate();
  const { location, locationName, loading: locationLoading, requestLocation, setManualLocation, needsManualLocation, dismissManualPrompt } = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const { t } = useT();
  const [query, setQuery] = useState(searchQuery);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [manualCity, setManualCity] = useState('');
  const [manualStreet, setManualStreet] = useState('');
  const [authModalOpen, setAuthModalOpen] = useState(false);
  
  // Suggestion modal state
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [suggestionType, setSuggestionType] = useState('');
  const [suggestionText, setSuggestionText] = useState('');
  const [sendingSuggestion, setSendingSuggestion] = useState(false);
  const [suggestionSent, setSuggestionSent] = useState(false);

  // Notifications state
  const [notifications, setNotifications] = useState({ total_new: 0, shops: [] });
  const [showNotifModal, setShowNotifModal] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    const fetchNotifications = async () => {
      try {
        const res = await axios.get(`${API}/favorites/notifications`, { withCredentials: true });
        if (!cancelled) {
          // Trigger browser notification only when count grows
          setNotifications((prev) => {
            if (
              res.data.total_new > 0 &&
              res.data.total_new > prev.total_new &&
              typeof window !== 'undefined' &&
              'Notification' in window &&
              Notification.permission === 'granted'
            ) {
              try {
                new Notification('Kyloo', {
                  body: t('header.notifications_new_other', { n: res.data.total_new }),
                  icon: LOGO_URL,
                  tag: 'kyloo-favorites'
                });
              } catch (e) {
                console.debug('Browser notification failed:', e);
              }
            }
            return res.data;
          });
        }
      } catch (e) {
        console.debug('Fetch notifications failed:', e);
      }
    };
    fetchNotifications();
    const id = setInterval(fetchNotifications, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isAuthenticated]);

  const handleOpenNotifications = () => {
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    setShowNotifModal(true);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      if (onSearch) {
        onSearch(query.trim());
      } else {
        navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      }
    }
  };

  const handleLocationClick = () => {
    setShowLocationModal(true);
  };

  const handleUseGPS = async () => {
    await requestLocation();
    setShowLocationModal(false);
  };

  const handleManualLocation = async () => {
    if (manualCity.trim()) {
      const ok = await setManualLocation(manualCity.trim(), manualStreet.trim());
      if (ok) {
        setManualCity('');
        setManualStreet('');
        setShowLocationModal(false);
      }
    }
  };

  // When GPS fails (denied / unavailable), automatically open the modal so
  // the user can fall back to city + street as initial position.
  useEffect(() => {
    if (needsManualLocation && !location) {
      setShowLocationModal(true);
    }
  }, [needsManualLocation, location]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleOpenSuggestion = () => {
    setSuggestionType('');
    setSuggestionText('');
    setSuggestionSent(false);
    setShowSuggestionModal(true);
  };

  const handleSendSuggestion = async () => {
    if (!suggestionType || !suggestionText.trim()) {
      toast.error(t('header.suggestion_error_empty'));
      return;
    }

    setSendingSuggestion(true);
    try {
      await axios.post(`${API}/suggestions/feedback`, {
        type: suggestionType,
        message: suggestionText.trim(),
        user_email: user?.email || 'guest'
      });
      
      setSuggestionSent(true);
      toast.success(t('header.suggestion_success_toast'));
    } catch (error) {
      console.error('Error sending suggestion:', error);
      setSuggestionSent(true);
      toast.success(t('header.suggestion_success_toast'));
    } finally {
      setSendingSuggestion(false);
    }
  };

  const closeSuggestionModal = () => {
    setShowSuggestionModal(false);
    setSuggestionType('');
    setSuggestionText('');
    setSuggestionSent(false);
  };

  return (
    <>
      <header className="bg-blue-600 sticky top-0 z-50 px-4 py-3 shadow-lg" data-testid="main-header">
        <div className="max-w-7xl mx-auto">
          {/* Top bar with logo, location, and user menu */}
          <div className="flex items-center justify-between mb-3">
            <Link to="/home" className="flex items-center gap-2" data-testid="logo-link">
              <img src={LOGO_URL} alt="Kyloo" className="h-8 w-auto" />
            </Link>
            
            <div className="flex items-center gap-3">
              {/* Location button */}
              <button
                onClick={handleLocationClick}
                className="flex items-center gap-1 text-white/90 hover:text-white transition-colors text-sm"
                data-testid="location-button"
              >
                <MapPin className="w-4 h-4" />
                <span className="max-w-[100px] truncate hidden sm:inline">
                  {locationLoading ? t('common.loading') : (locationName || t('header.location'))}
                </span>
              </button>

              {/* Language selector - visible to ALL users (guests included) */}
              <LanguageSelector variant="header" />

              {/* Admin shortcut: shop icon to quickly access dashboard or create shop */}
              {isAuthenticated && user?.is_admin && (
                <button
                  onClick={() => navigate(user?.has_shop ? '/dashboard' : '/negoziante')}
                  className="relative text-white/90 hover:text-white transition-colors p-1"
                  data-testid="admin-shop-shortcut"
                  aria-label={user?.has_shop ? t('header.menu_dashboard') : t('header.menu_become_pro')}
                  title={user?.has_shop ? t('header.menu_dashboard') : t('header.menu_become_pro')}
                >
                  <Store className="w-5 h-5" />
                  {!user?.has_shop && (
                    <span
                      className="absolute -top-0.5 -right-0.5 bg-orange-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none"
                      data-testid="admin-shop-shortcut-badge"
                    >
                      +
                    </span>
                  )}
                </button>
              )}

              {/* Notifications bell (only for authenticated users) */}
              {isAuthenticated && (
                <button
                  onClick={handleOpenNotifications}
                  className="relative text-white/90 hover:text-white transition-colors p-1"
                  data-testid="notifications-bell"
                  aria-label={t('header.notifications')}
                >
                  <Bell className="w-5 h-5" />
                  {notifications.total_new > 0 && (
                    <span
                      className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none"
                      data-testid="notifications-badge"
                    >
                      {notifications.total_new > 99 ? '99+' : notifications.total_new}
                    </span>
                  )}
                </button>
              )}

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-1 hover:opacity-90 transition-opacity"
                    data-testid="user-menu-button"
                  >
                    {isAuthenticated && user?.picture ? (
                      <img
                        src={user.picture}
                        alt={user.name}
                        className="w-9 h-9 rounded-full border-2 border-white shadow-md"
                        data-testid="user-profile-photo"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center border-2 border-white/30">
                        <User className="w-5 h-5 text-white" />
                      </div>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56" data-testid="user-menu-dropdown">
                  {isAuthenticated ? (
                    <>
                      <div className="px-3 py-2 border-b flex items-center gap-3">
                        {user?.picture && (
                          <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
                        )}
                        <div>
                          <p className="font-medium text-sm">{user?.name}</p>
                          <p className="text-xs text-slate-500">{user?.email}</p>
                        </div>
                      </div>
                      {user?.has_shop && (
                        <>
                          <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                            <Settings className="w-4 h-4 mr-2" />
                            {t('header.menu_dashboard')}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate('/dati-negozio')} data-testid="menu-dati-negozio">
                            <Store className="w-4 h-4 mr-2" />
                            Dati negozio
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate('/strumenti')} data-testid="menu-strumenti">
                            <Plus className="w-4 h-4 mr-2" />
                            Aggiungi strumenti
                          </DropdownMenuItem>
                        </>
                      )}
                      {user?.is_admin && (
                        <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                          <ShieldAlert className="w-4 h-4 mr-2 text-amber-500" />
                          {t('header.menu_admin')}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => navigate('/preferiti')} data-testid="menu-favorites">
                        <Heart className="w-4 h-4 mr-2" />
                        {t('header.menu_favorites')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleOpenSuggestion} data-testid="menu-suggestions">
                        <Lightbulb className="w-4 h-4 mr-2 text-amber-500" />
                        {t('header.menu_suggestions')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                        <LogOut className="w-4 h-4 mr-2" />
                        {t('header.menu_logout')}
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <>
                      <DropdownMenuItem onClick={() => setAuthModalOpen(true)} data-testid="menu-login">
                        <User className="w-4 h-4 mr-2" />
                        {t('header.menu_login')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/preferiti')} data-testid="menu-favorites-guest">
                        <Heart className="w-4 h-4 mr-2" />
                        {t('header.menu_favorites')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleOpenSuggestion} data-testid="menu-suggestions-guest">
                        <Lightbulb className="w-4 h-4 mr-2 text-amber-500" />
                        {t('header.menu_suggestions')}
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-slate-500">
                        <span className="text-xs">{t('header.continue_as_guest')}</span>
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Search bar */}
          {showSearch && (
            <form onSubmit={handleSearch} className="relative" data-testid="search-form">
              <div className="relative flex items-center">
                <Search className="absolute left-4 w-5 h-5 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder={t('header.search_placeholder')}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full pl-12 pr-12 py-3 bg-white rounded-full text-slate-800 placeholder-slate-400 search-input text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  data-testid="search-input"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    className="absolute right-14 p-1 text-slate-400 hover:text-slate-600"
                    data-testid="clear-search-button"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleLocationClick}
                  className={`absolute right-3 p-2 rounded-full transition-colors ${
                    location ? 'text-blue-600 bg-blue-50' : 'text-slate-400 hover:text-slate-600'
                  }`}
                  data-testid="location-pin-button"
                >
                  <MapPin className="w-5 h-5" />
                </button>
              </div>
            </form>
          )}
        </div>
      </header>

      {/* Location Modal */}
      <Dialog open={showLocationModal} onOpenChange={(open) => {
        setShowLocationModal(open);
        if (!open && needsManualLocation) dismissManualPrompt();
      }}>
        <DialogContent className="sm:max-w-md" data-testid="location-modal">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">{t('header.set_location_title')}</DialogTitle>
            {needsManualLocation && !location && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2" data-testid="gps-failed-hint">
                {t('header.gps_failed_hint')}
              </p>
            )}
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <Button
              onClick={handleUseGPS}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl flex items-center justify-center gap-2"
              disabled={locationLoading}
              data-testid="use-gps-button"
            >
              <MapPin className="w-5 h-5" />
              {locationLoading ? t('header.detecting') : t('header.use_gps')}
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-500">{t('common.or')}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Input
                placeholder={t('header.manual_city_placeholder')}
                value={manualCity}
                onChange={(e) => setManualCity(e.target.value)}
                className="py-3 rounded-xl"
                data-testid="manual-location-input"
              />
              <Input
                placeholder={t('header.manual_street_placeholder')}
                value={manualStreet}
                onChange={(e) => setManualStreet(e.target.value)}
                className="py-3 rounded-xl"
                data-testid="manual-street-input"
              />
              <Button
                onClick={handleManualLocation}
                variant="outline"
                className="w-full py-3 rounded-xl"
                disabled={!manualCity.trim() || locationLoading}
                data-testid="set-manual-location-button"
              >
                {locationLoading ? t('header.detecting') : t('header.confirm_location')}
              </Button>
            </div>

            {location && (
              <p className="text-sm text-slate-500 text-center">
                {t('header.current_location')} <span className="font-medium text-slate-700">{locationName}</span>
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Suggestion Modal */}
      <Dialog open={showSuggestionModal} onOpenChange={closeSuggestionModal}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="suggestion-modal">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl flex items-center gap-2 text-slate-800">
              <Lightbulb className="w-6 h-6 text-amber-500" />
              {t('header.suggestion_title')}
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              {t('header.suggestion_desc')}
            </DialogDescription>
          </DialogHeader>
          
          {!suggestionSent ? (
            <div className="space-y-4 pt-2">
              {/* Type selector */}
              <div>
                <Label className="text-sm font-medium text-slate-800">{t('header.suggestion_type')}</Label>
                <Select value={suggestionType} onValueChange={setSuggestionType}>
                  <SelectTrigger className="mt-1 rounded-xl bg-white border-slate-300 text-slate-800" data-testid="suggestion-type-select">
                    <SelectValue placeholder={t('header.suggestion_type_placeholder')} />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="bug">🐛 {t('header.suggestion_type_bug')}</SelectItem>
                    <SelectItem value="idea">💡 {t('header.suggestion_type_idea')}</SelectItem>
                    <SelectItem value="miglioramento">✨ {t('header.suggestion_type_improvement')}</SelectItem>
                    <SelectItem value="altro">📝 {t('header.suggestion_type_other')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Message textarea */}
              <div>
                <Label className="text-sm font-medium text-slate-800">{t('header.suggestion_message')}</Label>
                <Textarea
                  value={suggestionText}
                  onChange={(e) => setSuggestionText(e.target.value.slice(0, 300))}
                  placeholder={t('header.suggestion_placeholder')}
                  rows={4}
                  className="mt-1 rounded-xl resize-none bg-white border-slate-300 text-slate-800 placeholder-slate-400"
                  data-testid="suggestion-text-input"
                />
                <p className="text-xs text-slate-500 mt-1 text-right">
                  {suggestionText.length}/300
                </p>
              </div>

              {/* Motivational message */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <p className="text-sm text-amber-700 text-center italic">
                  ✨ {t('header.suggestion_motivational')}
                </p>
              </div>

              {/* Send button */}
              <Button
                onClick={handleSendSuggestion}
                disabled={!suggestionType || !suggestionText.trim() || sendingSuggestion}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl flex items-center justify-center gap-2"
                data-testid="send-suggestion-button"
              >
                {sendingSuggestion ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('header.suggestion_sending')}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    {t('header.suggestion_send')}
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🎉</span>
              </div>
              <h3 className="font-outfit font-semibold text-lg text-slate-800 mb-2">
                {t('header.suggestion_thanks_title')}
              </h3>
              <p className="text-slate-600 text-sm mb-4">
                {t('header.suggestion_thanks_msg')}
              </p>
              <Button
                onClick={closeSuggestionModal}
                variant="outline"
                className="rounded-xl border-slate-300 text-slate-700"
                data-testid="close-suggestion-button"
              >
                {t('common.close')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Notifications Modal */}
      <Dialog open={showNotifModal} onOpenChange={setShowNotifModal}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="notifications-modal">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl flex items-center gap-2 text-slate-800">
              <Bell className="w-6 h-6 text-blue-600" />
              {t('header.notifications')}
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              {notifications.total_new > 0
                ? t(notifications.total_new === 1 ? 'header.notifications_new_one' : 'header.notifications_new_other', { n: notifications.total_new })
                : t('header.notifications_count_zero')}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-2 pt-2">
            {notifications.shops.length === 0 ? (
              <div className="text-center py-8">
                <Bell className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">
                  {isAuthenticated
                    ? t('header.no_favorites')
                    : t('header.no_favorites_guest')}
                </p>
              </div>
            ) : (
              notifications.shops.map((s) => (
                <button
                  key={s.shop_id}
                  onClick={() => {
                    setShowNotifModal(false);
                    navigate(`/negozio/${s.shop_id}`);
                  }}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-blue-50 transition-colors text-left"
                  data-testid={`notif-shop-${s.shop_id}`}
                >
                  {s.shop_logo ? (
                    <img src={s.shop_logo} alt="" className="w-10 h-10 rounded-lg object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Store className="w-5 h-5 text-blue-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 truncate">{s.shop_nome}</p>
                    <p className="text-xs text-blue-600 font-semibold">
                      {s.new_products} {s.new_products === 1 ? t('header.new_products_one') : t('header.new_products_other')}
                    </p>
                  </div>
                  <span className="bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5">
                    {s.new_products}
                  </span>
                </button>
              ))
            )}
          </div>
          <Button
            onClick={() => {
              setShowNotifModal(false);
              navigate('/preferiti');
            }}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl mt-2"
            data-testid="notif-view-favorites"
          >
            <Heart className="w-4 h-4 mr-2" />
            {t('header.go_to_favorites')}
          </Button>
        </DialogContent>
      </Dialog>

      <AuthChoiceModal
        open={authModalOpen}
        onOpenChange={setAuthModalOpen}
        intent="user"
        initialMode="login"
      />
    </>
  );
}
